import { useEffect } from 'react';

type Props = {
  /**
   * Array of key combinations to handle (e.g., ['ctrl+r', 'shift+a'])
   */
  keys: string[];
  /**
   * Whether to handle events when focus is on form elements like inputs
   */
  handleFocusableElements?: boolean;
  /**
   * Handler to handle the key event
   */
  onKeyEvent: (key: string) => void;
};

/**
 * Custom hook to handle keyboard shortcuts
 */
export default function useKeyboardEventHandler({
  keys,
  handleFocusableElements = false,
  onKeyEvent,
}: Props) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!handleFocusableElements) {
        const target = e.target as HTMLElement;
        const tagName = target?.tagName?.toLowerCase();
        if (
          tagName === 'input' ||
          tagName === 'textarea' ||
          tagName === 'select' ||
          target?.isContentEditable
        ) {
          return;
        }
      }

      // Build key combination string (e.g., "ctrl+r")
      const keyCombo = [];
      if (e.ctrlKey) keyCombo.push('ctrl');
      if (e.altKey) keyCombo.push('alt');
      if (e.shiftKey) keyCombo.push('shift');
      if (e.metaKey) keyCombo.push('meta');

      const key = e.key?.toLowerCase();
      if (key && !['control', 'alt', 'shift', 'meta'].includes(key)) {
        keyCombo.push(key);
      }

      const keyComboStr = keyCombo.join('+');

      // Check if this key combination should be handled
      if (keys.includes(keyComboStr)) {
        e.preventDefault();
        onKeyEvent(keyComboStr);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [keys, handleFocusableElements, onKeyEvent]);
}
